import React, { useState, useEffect } from 'react';
import { SettingsService } from '../../services/SettingsService';
import { AIAvatarPreset } from '@shared/types/api';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

interface AIAvatarSelectorProps {
  model: 'gemini' | 'deepseek';
  title: string;
  onAvatarChange?: (avatarUrl: string) => void;
}

export const AIAvatarSelector: React.FC<AIAvatarSelectorProps> = ({
  model,
  title,
  onAvatarChange
}) => {
  const [presets, setPresets] = useState<AIAvatarPreset[]>([]);
  const [currentAvatar, setCurrentAvatar] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const { toast, error, hideToast } = useToast();

  useEffect(() => {
    loadData();
  }, [model]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 并行加载预设列表和当前头像
      const [presetsResponse, currentResponse] = await Promise.all([
        SettingsService.getAIAvatarPresets(),
        SettingsService.getAIAvatar(model)
      ]);
      
      setPresets(presetsResponse.presets);
      setCurrentAvatar(currentResponse.url);
      
      // 找到当前选中的预设
      const currentPreset = presetsResponse.presets.find(
        (preset: AIAvatarPreset) => currentResponse.url.includes(preset.id)
      );
      if (currentPreset) {
        setSelectedPreset(currentPreset.id);
      }
    } catch (error) {
      console.error('Failed to load AI avatar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = async (presetId: string) => {
    if (selecting || presetId === selectedPreset) return;
    
    try {
      setSelecting(true);
      const response = await SettingsService.selectAIAvatarPreset(model, presetId);
      
      console.log('[AIAvatarSelector] Select response:', response);
      
      if (response && response.avatarUrl) {
        console.log('[AIAvatarSelector] Updating avatar to:', response.avatarUrl);
        setSelectedPreset(presetId);
        setCurrentAvatar(response.avatarUrl);
        onAvatarChange?.(response.avatarUrl);
      }
    } catch (err) {
      console.error('Failed to select avatar preset:', err);
      error('选择头像失败，请重试');
    } finally {
      setSelecting(false);
    }
  };

  if (loading) {
    return (
      <div className="ai-avatar-selector loading">
        <h3>{title}</h3>
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  return (
    <div className="ai-avatar-selector">
      <h3>{title}</h3>
      
      {/* 当前头像预览 */}
      <div className="current-avatar">
        <span>当前头像：</span>
        <img 
          key={currentAvatar}
          src={`${currentAvatar}?t=${Date.now()}`}
          alt={`${title}当前头像`}
          className="avatar-preview"
        />
      </div>
      
      {/* 预设头像网格 */}
      <div className="presets-grid">
        <span className="grid-label">选择预设：</span>
        <div className="avatars-grid">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className={`avatar-option ${
                selectedPreset === preset.id ? 'selected' : ''
              } ${selecting ? 'disabled' : ''}`}
              onClick={() => handleSelectPreset(preset.id)}
            >
              <img 
                src={preset.url} 
                alt={`预设头像 ${preset.id}`}
                className="preset-avatar"
              />
              {selectedPreset === preset.id && (
                <div className="selected-indicator">✓</div>
              )}
              {preset.isDefault && (
                <div className="default-badge">默认</div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {selecting && (
        <div className="selecting-overlay">
          <div className="selecting-message">正在设置...</div>
        </div>
      )}

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
